import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { tokens, Text } from '@hotjar/hotsauce';

import { CollectionsSelector } from '@hotjar/webapp/features/Collections';
import { event } from '@hotjar/webapp/tracking/tools/hotjar';

import { useHighlightCollections } from '../use-highlight-collections';
import { SkeletonLoader } from './SkeletonLoader';

const RECORDING_HIGHLIGHT_ADDED_TO_COLLECTION = 'recording_highlight_added_to_collection';
const HEATMAP_HIGHLIGHT_ADDED_TO_COLLECTION = 'heatmap_highlight_added_to_collection';

const HeadingSkeleton = styled(SkeletonLoader)`
  width: 110px;
  height: 20px;
  margin-top: ${tokens.spacer.x1};
  margin-bottom: ${tokens.spacer.x0pt5};
`;

const CollectionsInputSkeleton = styled(SkeletonLoader)`
  width: 260px;
  height: 56px;
`;

const Wrapper = styled.section`
  padding: ${tokens.spacer.x2};
`;

const Heading = styled(Text)`
  margin-bottom: ${tokens.spacer.x0};
  margin-top: ${tokens.spacer.x1};
`;

type CollectionId = string;

function createCollectionsSet(collections: Array<{ id: CollectionId }>): Set<CollectionId> {
  return new Set(collections.map((collection) => collection.id));
}
interface CollectionsProps {
  isLoading: boolean;
  highlightId: string;
  highlightCollections: Array<{ id: CollectionId }>;
  highlightType: string | null;
  siteId: string;
}

export function Collections({
  isLoading = false,
  highlightId,
  highlightCollections,
  highlightType,
  siteId,
}: CollectionsProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<Set<CollectionId>>(
    createCollectionsSet(highlightCollections),
  );
  const { add, remove } = useHighlightCollections(siteId, highlightId);

  useEffect(() => {
    setSelectedCollectionIds(createCollectionsSet(highlightCollections));
  }, [highlightCollections]);

  const handleEventSend = (): void => {
    if (highlightType === 'heatmap') {
      event(HEATMAP_HIGHLIGHT_ADDED_TO_COLLECTION);
    }

    if (highlightType === 'recording') {
      event(RECORDING_HIGHLIGHT_ADDED_TO_COLLECTION);
    }
  };

  if (isLoading) {
    return (
      <Wrapper>
        <HeadingSkeleton />
        <CollectionsInputSkeleton />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Heading as="p" textType="subtitle2">
        {t('highlights:single.collections')}
      </Heading>
      <CollectionsSelector
        onAdd={add}
        onEventSend={handleEventSend}
        onRemove={remove}
        selectedCollectionIds={selectedCollectionIds}
        onSelectedIdsChange={setSelectedCollectionIds}
        siteId={siteId}
      />
    </Wrapper>
  );
}
